import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { features } from "@/lib/features";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { useCanEdit } from "@/lib/project-access";
import { Button } from "@/components/ui/button";
import { Kicker } from "@/components/ui/kicker";
import { Tonal } from "@/components/ui/tonal";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ScenarioRow } from "@/components/experience/scenario-row";
import { GenerateScenarioModal } from "@/components/experience/generate-scenario-modal";

export interface LinkedScenarioItem {
  id: string;
  outlineId: string;
  name: string;
  description: string | null;
}

interface PersonaScenariosSectionProps {
  projectId: string;
  personaId: string;
  firstName: string;
  linkedScenarios: LinkedScenarioItem[];
}

export function PersonaScenariosSection({
  projectId,
  personaId,
  firstName,
  linkedScenarios,
}: PersonaScenariosSectionProps) {
  const canEdit = useCanEdit();
  const [showCastDialog, setShowCastDialog] = useState(false);
  const [showNewScenario, setShowNewScenario] = useState(false);

  const attachedOutlineIds = useMemo(
    () => linkedScenarios.map((s) => s.outlineId),
    [linkedScenarios],
  );

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <Kicker as="h2">Scenarios</Kicker>
          <span className="text-sm text-muted-foreground">
            Situations {firstName} is the lens for
          </span>
        </div>
        {canEdit && linkedScenarios.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="xs"
              variant="outline"
              onClick={() => setShowCastDialog(true)}
            >
              <Plus className="mr-1.5 h-3 w-3" aria-hidden="true" />
              Cast into scenario
            </Button>
            <Button
              size="xs"
              variant="outline"
              onClick={() => setShowNewScenario(true)}
            >
              <Plus className="mr-1.5 h-3 w-3" aria-hidden="true" />
              New scenario
            </Button>
          </div>
        )}
      </div>

      {linkedScenarios.length === 0 ? (
        canEdit ? (
          <Tonal padding="md" className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Not attached to any scenarios yet. Cast {firstName} into an
              existing scenario or create a new one to run walkthroughs through
              this lens.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="xs"
                variant="outline"
                onClick={() => setShowCastDialog(true)}
              >
                <Plus className="mr-1.5 h-3 w-3" aria-hidden="true" />
                Cast into existing scenario
              </Button>
              <Button
                size="xs"
                variant="outline"
                onClick={() => setShowNewScenario(true)}
              >
                <Plus className="mr-1.5 h-3 w-3" aria-hidden="true" />
                New scenario for {firstName}
              </Button>
            </div>
          </Tonal>
        ) : (
          <p className="text-sm text-muted-foreground">
            Not attached to any scenarios yet.
          </p>
        )
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-hidden divide-y divide-border">
          {linkedScenarios.map((scenario, i) => (
            <ScenarioRow
              key={scenario.id}
              variant="compact"
              scenario={{
                id: scenario.id,
                name: scenario.name,
                description: scenario.description,
              }}
              href={`/projects/${projectId}/scenarios/${scenario.outlineId}#persona-${personaId}`}
              leading={
                <span className="w-6 shrink-0 text-xs tabular-nums text-muted-foreground">
                  {String(i + 1).padStart(2, "0")}
                </span>
              }
              actions={
                features.liveWalkthrough && canEdit ? (
                  <Button
                    asChild
                    variant="outline"
                    size="xs"
                    className="shrink-0"
                  >
                    <Link
                      to={`/projects/${projectId}/live-walkthrough?personaId=${personaId}&scenarioId=${scenario.id}`}
                    >
                      Launch →
                    </Link>
                  </Button>
                ) : null
              }
            />
          ))}
        </div>
      )}

      {canEdit && (
        <CastIntoScenarioDialog
          open={showCastDialog}
          onOpenChange={setShowCastDialog}
          projectId={projectId}
          personaId={personaId}
          firstName={firstName}
          excludeOutlineIds={attachedOutlineIds}
        />
      )}

      {canEdit && showNewScenario && (
        <GenerateScenarioModal
          projectId={projectId}
          initialMode="manual"
          initialPersonaId={personaId}
          onClose={() => setShowNewScenario(false)}
        />
      )}
    </section>
  );
}

function CastIntoScenarioDialog({
  open,
  onOpenChange,
  projectId,
  personaId,
  firstName,
  excludeOutlineIds,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  personaId: string;
  firstName: string;
  excludeOutlineIds: string[];
}) {
  const [outlineId, setOutlineId] = useState("");
  const [brief, setBrief] = useState("");
  const utils = trpc.useUtils();

  const { data: outlinesList } = trpc.outline.list.useQuery(
    { projectId },
    { enabled: open && !!projectId },
  );

  const excluded = useMemo(
    () => new Set(excludeOutlineIds),
    [excludeOutlineIds],
  );
  const availableOutlines = useMemo(
    () => (outlinesList ?? []).filter((o) => !excluded.has(o.id)),
    [outlinesList, excluded],
  );

  useEffect(() => {
    if (!open) {
      setOutlineId("");
      setBrief("");
    }
  }, [open]);

  const createScenario = trpc.scenario.create.useMutation({
    onSuccess: () => {
      void utils.persona.getById.invalidate({ id: personaId });
      void utils.outline.list.invalidate({ projectId });
      if (outlineId) {
        void utils.outline.getById.invalidate({ id: outlineId });
      }
      toast.success(`Cast ${firstName} into scenario`);
      onOpenChange(false);
    },
    onError: (err) => toastTrpcError(err, "Couldn't cast persona"),
  });

  const noEligible = outlinesList && availableOutlines.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cast {firstName} into a scenario</DialogTitle>
          <DialogDescription>
            Choose an existing scenario to evaluate through {firstName}'s lens.
          </DialogDescription>
        </DialogHeader>

        {noEligible ? (
          <p className="text-sm text-muted-foreground">
            {firstName} is already cast on every scenario in this project.
          </p>
        ) : (
          <div className="min-w-0 space-y-4">
            <div className="min-w-0 space-y-2">
              <Label htmlFor="cast-into-outline-select">Scenario</Label>
              <Select value={outlineId} onValueChange={setOutlineId}>
                <SelectTrigger
                  id="cast-into-outline-select"
                  className="w-full min-w-0 max-w-full overflow-hidden"
                >
                  <SelectValue placeholder="Select a scenario" />
                </SelectTrigger>
                <SelectContent
                  position="popper"
                  className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)]"
                >
                  {availableOutlines.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {outlineId && (
              <div className="space-y-1.5">
                <Label htmlFor="cast-into-outline-brief">
                  First-person brief (optional)
                </Label>
                <Textarea
                  id="cast-into-outline-brief"
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  rows={3}
                  placeholder={`Write as ${firstName} in first person — what brought them here and what's on their mind...`}
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!outlineId) return;
              createScenario.mutate({
                outlineId,
                personaId,
                brief: brief.trim() || undefined,
              });
            }}
            disabled={!outlineId || createScenario.isPending || !!noEligible}
          >
            {createScenario.isPending ? "Casting…" : "Cast persona"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
